import { ref, shallowRef } from 'vue';
import type {
  ExperimentClient,
  ExperimentConfig,
} from '@amplitude/experiment-js-client';

import { debug } from '../debug';

type AnalyticsModule = typeof import('@amplitude/analytics-browser');

/**
 * Amplitude is loaded lazily so the SDK (~67 KB gz) stays off the critical
 * load path: a static import would force it into the eager `modulepreload`
 * before first paint even though analytics has no reason to be synchronous.
 * The public API below is unchanged for call sites; the SDK is fetched behind
 * an async boundary the first time any facade function runs.
 */
let analyticsModulePromise: Promise<AnalyticsModule> | null = null;
/**
 * Synchronous handle to the loaded module. `getDeviceId` runs inside a
 * `{ synchronous: true }` axios interceptor and cannot await, so it reads this
 * handle directly and returns `null` until the SDK has finished loading (the
 * device-id header is best-effort and amplitude already inits after pfm).
 */
let analyticsModule: AnalyticsModule | null = null;

const loadAnalytics = () => {
  if (!analyticsModulePromise) {
    analyticsModulePromise = import('@amplitude/analytics-browser').then(
      module => {
        analyticsModule = module;
        return module;
      },
      err => {
        /**
         * Do not keep a rejected promise in the cache: every later caller would
         * be handed the same rejection, so one failed chunk fetch left analytics
         * dead for the rest of the session. Clearing it lets a later call try
         * again. Note this only recovers across page loads — within one document
         * the browser memoises the failed module URL in its module map and will
         * not re-fetch it, so the next attempt fails without hitting the network.
         */
        analyticsModulePromise = null;
        throw err;
      },
    );
  }
  return analyticsModulePromise;
};

/**
 * Runs `action` with the lazily-loaded SDK, skipping it if the SDK is not
 * available.
 *
 * Failing to *load* the SDK is an environmental condition, not a bug: the chunk
 * fetch drops on a flaky mobile connection. Since these facade functions are
 * fire-and-forget at nearly every call site (no `await`, no `.catch`), letting
 * that reject surfaced it as an unhandled promise rejection instead of a quietly
 * skipped event. So the load failure is logged and swallowed.
 *
 * An exception thrown by `action` itself is deliberately NOT caught — that would
 * be a bug at the call site (a bad payload, say), and swallowing it here would
 * erase it from production entirely, since `debug` does not log there.
 */
const withAnalytics = async <T>(
  action: (module: AnalyticsModule) => T,
  context: string,
): Promise<T | undefined> => {
  let module: AnalyticsModule;

  try {
    module = await loadAnalytics();
  } catch (err) {
    debug('Amplitude', `${context} skipped, SDK unavailable`, err);
    return undefined;
  }

  return action(module);
};

export const setUserId = async (userId: string) => {
  debug('Amplitude', 'setUserId', userId);
  await withAnalytics(({ setUserId: _setUserId }) => {
    _setUserId(userId);
  }, 'setUserId');
};

/** Resets user and device ids */
export const invalidateUser = async () => {
  debug('Amplitude', 'invalidateUser');
  await withAnalytics(({ reset }) => {
    reset();
  }, 'invalidateUser');
};

/**
 * Outstanding `logEvent` calls whose `track()` has not run yet (they are still
 * awaiting the lazy SDK load). `flushEvents` awaits these before flushing so a
 * synchronous `flush()` cannot outrun an event logged immediately before it.
 */
const pendingEvents = new Set<Promise<void>>();

export const logEvent = async (
  event: string,
  properties?: Record<string, unknown>,
) => {
  debug(
    { prefix: 'Amplitude', shouldLogInStageEnv: true },
    'logEvent',
    event,
    properties,
  );
  /**
   * Registered in `pendingEvents` before being awaited, so a `flushEvents()`
   * running concurrently can wait for this `track()` to be enqueued. Goes
   * through `withAnalytics` rather than `loadAnalytics` directly: this is a
   * fire-and-forget call at nearly every call site, so a rejected load has to be
   * swallowed here instead of surfacing as an unhandled rejection.
   */
  const pending = withAnalytics(({ track }) => {
    track(event, properties);
  }, 'logEvent');
  pendingEvents.add(pending);
  try {
    await pending;
  } finally {
    pendingEvents.delete(pending);
  }
};

/**
 * Sets user properties that will be attached to all subsequent events for the
 * current user. Use this for stable, cross-event attributes (e.g. A/B test
 * variant) so you don't have to thread them through every event call site.
 */
export const setUserProperties = async (
  properties: Record<string, unknown>,
) => {
  debug(
    { prefix: 'Amplitude', shouldLogInStageEnv: true },
    'setUserProperties',
    properties,
  );
  await withAnalytics(({ Identify, identify }) => {
    const identifyEvent = new Identify();
    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined) {
        continue;
      }
      identifyEvent.set(key, value as never);
    }
    identify(identifyEvent);
  }, 'setUserProperties');
};

/**
 * Forces queued events to be sent immediately and resolves once the flush
 * completes. Use before navigating away (e.g. `window.location.href = ...`)
 * to ensure pending events reach Amplitude before the page is torn down.
 * Never rejects — navigation should not be blocked by analytics failures.
 * Resolves immediately if the SDK was never loaded (nothing to flush).
 */
export const flushEvents = async () => {
  // Wait for any in-flight logEvent() calls to enqueue their track() before
  // flushing, so events logged right before an `await flushEvents()` (e.g.
  // ahead of a `window.location` navigation) are included in the flush rather
  // than enqueued after it has already run.
  if (pendingEvents.size > 0) {
    await Promise.allSettled([...pendingEvents]);
  }
  if (!analyticsModule) {
    return;
  }
  return analyticsModule.flush().promise.catch(err => {
    debug('Amplitude', 'flushEvents failed', err);
  });
};

export const getDeviceId = () => {
  /**
   * There is no amplitude instance in TEST mode while running vitest
   * That's why we check for instance here
   * Otherwise we would want to mock amplitude instance whenever pfm is used in tests
   *
   * Also returns null until the SDK has lazily loaded, since this runs inside a
   * synchronous axios interceptor that cannot await.
   */
  return analyticsModule?.getDeviceId() ?? null;
};

/**
 * Resolves to `undefined` (rather than the SDK's return value) when the SDK
 * could not be loaded.
 */
export const addPlugin = async (...args: Parameters<AnalyticsModule['add']>) =>
  withAnalytics(({ add }) => add(...args), 'addPlugin');

export const init = async (amplitudeApiKey: string) => {
  const { init: _init } = await loadAnalytics();
  return _init(amplitudeApiKey, { defaultTracking: false }).promise.then(
    () => debug('Amplitude', 'Client initialized'),
    err => {
      debug('Amplitude', 'Failed to init client');
      throw err;
    },
  );
};

/**
 * @description Keep the Experiment client in a shallow ref so Vue does not
 * wrap the SDK internals, preventing recursive updates when the client mutates
 * itself
 */
const experiment = shallowRef<ExperimentClient | null>(null);
const isExperimentLoading = ref(true);

type ExperimentModule = typeof import('@amplitude/experiment-js-client');

let experimentModulePromise: Promise<ExperimentModule> | null = null;

const loadExperiment = () => {
  if (!experimentModulePromise) {
    experimentModulePromise = import('@amplitude/experiment-js-client').catch(
      err => {
        // Same reason as loadAnalytics: never leave a rejected promise cached,
        // so a later initExperiment can retry instead of inheriting the failure.
        experimentModulePromise = null;
        throw err;
      },
    );
  }
  return experimentModulePromise;
};

export function useAmplitudeExperiment() {
  const initExperiment = (
    amplitudeApiKey: string,
    config?: ExperimentConfig,
  ) => {
    loadExperiment()
      .then(({ Experiment }) => {
        const client = Experiment.initializeWithAmplitudeAnalytics(
          amplitudeApiKey,
          config,
        );
        experiment.value = client;
        client
          .start()
          .catch(err => debug('Amplitude', 'Experiment start failed', err))
          .finally(() => {
            isExperimentLoading.value = false;
          });
        debug('Amplitude', 'Experiment initialized');
      })
      /**
       * Consumers gate on `isExperimentLoading` before picking a variant (e.g.
       * freedom-tours search results), so leaving it `true` would stall them
       * indefinitely — a failed chunk load on a flaky mobile-webview connection
       * would strand experiment-gated UI in a permanent loading state. Release
       * the gate so they fall through to the default, non-variant path, matching
       * the pre-lazy-load behaviour where the client was created synchronously
       * and its `.finally` always ran. Chained rather than passed as `then`'s
       * second argument so it also covers a throw from initializing the client,
       * not just a failed SDK load.
       */
      .catch(err => {
        isExperimentLoading.value = false;
        debug('Amplitude', 'Experiment init skipped', err);
      });
  };

  return { experiment, initExperiment, isExperimentLoading };
}
